import {
  Box,
  Typography,
  CssBaseline,
  ThemeProvider,
  useTheme,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple, faWindows } from "@fortawesome/free-brands-svg-icons";
import { downloadFile } from "../../helper/utils";
import { useLoginValidator } from "../../pages/GadgetDetails/hooks/useLoginValidator";
import { useEffect } from "react";
import { useAppDispatch } from "../../common/state/hooks";
import { update } from "../../common/state/Slices/HeaderMenuSlice";
import { Routes } from "../../routes/routes";
import styles from "./Downloads.module.css";
import { MacOSHelpGuide } from "../MacOSHelpGuide";
import { InstallationGuide } from "../InstallationGuide";

declare const genv: any;

export const Downloads = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { validateLogin } = useLoginValidator();
  const downloadPackage = (installerUrl: string) =>
    downloadFile(installerUrl, `installer`);
  const handleDownload = (installerUrl: string) =>
    validateLogin(downloadPackage, [installerUrl]);

  useEffect(() => {
    dispatch(
      update({
        selectedMenu: Routes.DOWNLOADS,
      })
    );
  }, [dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className={styles.container}>
        <Box className={styles.headerSection}>
          <Typography variant="h4" className={styles.mainText}>
            Experience Garuda2x: Trial Version Download
          </Typography>
          <Typography variant="body1" className={styles.secondaryText}>
            Unlock the potential of connectivity and automation with the
            Garuda2x Trial Version. This limited version is preloaded with
            select Agents to give you a glimpse of how the platform can
            streamline your workflows and revolutionize your processes in the
            pharmaceutical and biomedical domains.
          </Typography>
        </Box>

        <Box className={styles.contentSection}>
          <Typography
            variant="h5"
            color="primary"
            className={styles.sectionHeader}
          >
            What’s Included in the Trial Version?
          </Typography>
          <ul className={styles.featuresList}>
            <li>
              Prepackaged Agents: Explore a set of ready-to-use Agents designed
              to demonstrate the core functionalities of Garuda2x. These Agents
              allow you to try workflows without additional setup or
              customization.
            </li>
            <li>
              Recipe Runner: Run pre-made workflows (Recipes) using the
              intuitive Recipe Player. These workflows are preconfigured to
              demonstrate the interoperability and automation capabilities of
              Garuda2x.
            </li>
            <li>
              Core Features: Discover how Garuda2x connects tools, facilitates
              interoperability, and simplifies operations with its cutting-edge
              technology.
            </li>
          </ul>
        </Box>

        <Box className={styles.contentSection}>
          <Typography
            variant="h5"
            color="primary"
            className={styles.sectionHeader}
          >
            Limitations of the Trial Version
          </Typography>
          <ul className={styles.featuresList}>
            <li>
              No Recipe Builder: The trial version does not include the Recipe
              Builder, so users cannot create or modify workflows. This feature
              is available in the full version of Garuda2x.
            </li>
            <li>
              No Custom Agent Integration: The trial version does not allow
              users to add their own Agents or tools to the platform. To unlock
              this feature, contact us for the full version or custom solutions.
            </li>
            <li>
              Limited Agent Library: he prepackaged Agents in the trial version
              represent only a fraction of the full Garuda2x ecosystem.
              Additional Agents and advanced functionalities are available in
              the full version.
            </li>
          </ul>
        </Box>

        <Box className={styles.contentSection}>
          <Typography
            variant="h5"
            color="primary"
            className={styles.sectionHeader}
          >
            How to Get Started
          </Typography>

          <Box>
            <ul className={styles.featuresList}>
              <li>Download the Trial Version:</li>
              <div
                className={styles.downloadLink}
                onClick={() =>
                  handleDownload(genv.DESKTOP_CLIENT_WINDOWS_INSTALLER_URL)
                }
              >
                <FontAwesomeIcon icon={faWindows} className={styles.icon} />
                <Typography
                  variant="body2"
                  className={styles.downloadLinkText}
                  sx={{ marginBottom: 0 }}
                >
                  {" "}
                  Click here to download Garuda2x Trial Version for Windows
                </Typography>
              </div>
              <InstallationGuide
                url={genv.DESKTOP_CLIENT_WINDOWS_INSTALLATION_GUIDE_URL}
              />
              <div
                className={styles.downloadLink}
                onClick={() =>
                  handleDownload(genv.DESKTOP_CLIENT_MAC_INSTALLER_URL)
                }
              >
                <FontAwesomeIcon icon={faApple} className={styles.icon} />
                <Typography variant="body2" className={styles.downloadLinkText}>
                  {" "}
                  Click here to download Garuda2x Trial Version for macOS
                </Typography>
              </div>
              <InstallationGuide
                url={genv.DESKTOP_CLIENT_MAC_INSTALLATION_GUIDE_URL}
              />
              <Box sx={{ ml: 4, mb: 1, mt: -1 }}>
                <MacOSHelpGuide />
              </Box>

              <li>
                Install and Explore: Follow the provided installation
                instructions to set up Garuda2x on your system. Begin
                experimenting with workflows using the prepackaged Agents.
              </li>
              <li>
                Experience the Core Features: Navigate through the Recipe
                Builder and Recipe Runner to understand how Garuda2x simplifies
                connectivity and automation.
              </li>
            </ul>
          </Box>
        </Box>

        <Box className={styles.footerSection}>
          <Typography variant="h6">
            Need More? Contact Us for Custom Solutions. The trial version is
            just the beginning. To unlock the full potential of Garuda2x and
            integrate custom Agents tailored to your specific needs, reach out
            to us at : <a href="mailto:info@sbx-corp.com">info@sbx-corp.com</a>
          </Typography>
          <Typography variant="h6">
            Our dedicated team can assist you in building bespoke solutions or
            upgrading to the full version of Garuda2x.
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
